"use client";

import { useState } from "react";
import { BiImageAlt } from "react-icons/bi";
import type { CardOrientation } from "@/interfaces/GameHandler";
import {
  CARD_ASPECT_RATIO,
  LANDSCAPE_CARD_ASPECT_RATIO,
  artSizeIn,
} from "@/lib/cardArt";

/**
 * Card art. The one place on the site that turns an image url into a card.
 *
 * Every surface used to draw its own: the set grid, the deck builder pool, the
 * deck zones, the decklists, the card pages and half a dozen thumbnails each had
 * their own markup and their own idea of how big a card is. They disagreed, and
 * the same two bugs kept reappearing in the ones that were written last:
 *
 * 1. Reserving space with a fixed `min-h-[155px] sm:min-h-[220px]`. These grids
 *    are fluid -- a card is a third of the viewport on mobile and a sixth on
 *    desktop -- so the reserved box almost never matched the height the image
 *    took, and the grid jumped as images loaded. An aspect-ratio box is correct
 *    at every width.
 *
 * 2. Using HeroUI's <Image>, which renders `opacity-0` and only reveals the
 *    image once its own onLoad handler sets `data-loaded=true`. A cached image is
 *    already `complete` before React attaches that handler, so onLoad never fires
 *    and the card stays invisible -- fully loaded, correctly sized, transparent.
 *    A plain <img> has no such state to get stuck in.
 *
 * It also knows that not every card is portrait. A game may print some of its
 * cards sideways (Palworld's Structures), and those are stored as printed. A
 * sideways card keeps a full portrait slot so grids stay on one rhythm, and its
 * art fills the slot's width and sits across the middle of it -- see lib/cardArt.
 */

export interface CardArtProps {
  src: string;
  alt: string;
  /** Portrait unless the card's game says otherwise. */
  orientation?: CardOrientation;
  /**
   * Slot width in px. Omit to fill the container, which is what a fluid grid
   * wants; the slot then takes its height from the card ratio either way.
   *
   * Leaving it out means the parent must have a width of its own. A shrink-to-fit
   * parent -- a `<button>`, which is what HeroUI renders a pressable Card as --
   * has no width until its contents give it one, and the art here cannot: it is
   * positioned out of flow so the empty slot can hold the card's shape before
   * the image loads. Parent and card then both collapse to nothing. Give such a
   * parent `w-full`, or pass a width here.
   */
  width?: number;
  /** Slot height in px, when it should not follow from `width`. */
  height?: number;
  loading?: "lazy" | "eager";
  fetchPriority?: "high" | "low" | "auto";
  /** Classes for the slot. */
  className?: string;
  /** Classes for the art itself -- outlines, brightness, opacity. */
  imgClassName?: string;
  /** Inline styles for the art, for the values that have to be computed. */
  imgStyle?: React.CSSProperties;
  /**
   * Whether the empty slot is filled while the art loads. A landscape card does
   * not cover its slot, so a background there reads as two bars rather than as a
   * card; callers that want one anyway can ask.
   */
  background?: string;
  /** Rounding, matched between the slot and the art. */
  radiusClassName?: string;
}

export default function CardArt({
  src,
  alt,
  orientation = "portrait",
  width,
  height,
  loading = "lazy",
  fetchPriority,
  className,
  imgClassName,
  imgStyle,
  background = "bg-gray-200 dark:bg-gray-800",
  radiusClassName = "rounded-md",
}: CardArtProps) {
  const [hasError, setHasError] = useState(false);

  const isLandscape = orientation === "landscape";

  // A fixed-width slot sizes the art in px so it lines up with its neighbours
  // exactly; a fluid one leaves it to the aspect-ratio box.
  const art = width ? artSizeIn(width, height ?? 0, orientation) : undefined;

  const slotStyle: React.CSSProperties = width
    ? {
        width,
        height,
        aspectRatio: height ? undefined : CARD_ASPECT_RATIO,
      }
    : { aspectRatio: CARD_ASPECT_RATIO };

  return (
    <div
      className={`relative flex items-center justify-center overflow-hidden ${
        width ? "shrink-0" : "w-full"
      } ${radiusClassName} ${isLandscape ? "" : background} ${className ?? ""}`}
      style={slotStyle}
    >
      {hasError ? (
        <div className="flex h-full w-full items-center justify-center">
          <BiImageAlt className="text-gray-500" size={40} />
        </div>
      ) : (
        /* eslint-disable-next-line @next/next/no-img-element -- deliberate: see the note above on HeroUI's <Image> and cached art. */
        <img
          src={src}
          alt={alt}
          loading={loading}
          fetchPriority={fetchPriority}
          decoding="async"
          width={art?.width}
          height={art?.height}
          className={`${radiusClassName} ${
            isLandscape
              ? `w-full object-contain ${background}`
              : "absolute inset-0 h-full w-full object-cover"
          } ${imgClassName ?? ""}`}
          style={{
            ...(isLandscape
              ? { aspectRatio: LANDSCAPE_CARD_ASPECT_RATIO }
              : undefined),
            ...imgStyle,
          }}
          onError={() => setHasError(true)}
        />
      )}
    </div>
  );
}
